import { useEffect, useRef, useState } from 'react'
import { useInView, animate } from 'framer-motion'

interface AnimatedNumberProps {
  /** 목표 숫자 */
  value: number
  /** 애니메이션 지속 시간 (ms, 기본 800) */
  duration?: number
  /** 숫자 포맷 함수 (기본: 천단위 콤마) */
  format?: (value: number) => string
  /** CSS 클래스 */
  className?: string
}

/** 기본 포맷: 천단위 콤마 */
function defaultFormat(value: number): string {
  return Math.round(value).toLocaleString('ko-KR')
}

/**
 * 숫자 카운팅 애니메이션 컴포넌트.
 *
 * 0부터 목표값까지 부드럽게 카운팅됩니다.
 * 뷰포트에 진입할 때 애니메이션이 시작됩니다.
 *
 * @example
 * ```tsx
 * <AnimatedNumber value={1285620} format={(v) => `${Math.round(v).toLocaleString()}원`} />
 * ```
 */
export default function AnimatedNumber({
  value,
  duration = 800,
  format = defaultFormat,
  className,
}: AnimatedNumberProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-50px' })
  const [displayValue, setDisplayValue] = useState('0')

  useEffect(() => {
    if (!isInView) return

    // prefers-reduced-motion 체크
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      setDisplayValue(format(value))
      return
    }

    const controls = animate(0, value, {
      duration: duration / 1000,
      ease: [0.25, 0.1, 0.25, 1],
      onUpdate(latest) {
        setDisplayValue(format(latest))
      },
      onComplete() {
        setDisplayValue(format(value))
      },
    })

    return () => controls.stop()
  }, [isInView, value, duration, format])

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  )
}
